import { DataTableHeaderCell } from '@/_shared/components/data-table/header/index.js';
import { DataTable } from '@/_shared/components/data-table/index.js';
import { Hint } from '@/_shared/components/hint/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Body1, Button, Dropdown, Option, Spinner, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import type { FiledClaim } from '@this/shared/dist/apis/serviceaddinapi/filed-claims.js';
import { fromIso, toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { formatEur } from '@this/shared/dist/tools/number-format.js';
import { useCallback, useMemo, useState } from 'react';
import { useStyles } from './_styles.js';

const columnHelper = createColumnHelper<FiledClaim>();

export function FiledClaims() {
    const styles = useStyles();
    const tsr = useTsRest();
    const navigate = useNavigate();

    // Year filter state
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);
    const [dateField, setDateField] = useState<'reportDate' | 'claimDate'>('reportDate');

    // Generate array of 7 years (current year and 6 previous years)
    const yearOptions = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => currentYear - i);
    }, [currentYear]);

    const { data, isLoading, error } = tsr.filedClaims.getFiledClaims.useQuery({
        queryKey: ['filed-claims', year, dateField],
        queryData: {
            query: { year, dateField },
        },
        refetchInterval: 30_000,
    });

    const handleNewClaimClick = useCallback(() => {
        navigate({ to: '/new-claim' });
    }, [navigate]);

    const handleClaimClick = useCallback(
        (claimId: string) => {
            navigate({ to: '/claim-details/$claimId', params: { claimId } });
        },
        [navigate]
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('claimNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadennummer"
                    />
                ),
                cell: (info) => (
                    <span
                        onClick={() => handleClaimClick(info.row.original.id)}
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                    >
                        {info.row.original.claimNumber}
                    </span>
                ),
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('status', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Status"
                    />
                ),
                cell: (info) => info.row.original.status || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('contractNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Vertrag"
                        tooltip="Vertragsnummer"
                    />
                ),
                cell: (info) => info.row.original.contractNumber || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('policyHolder', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Versicherungsnehmer"
                    />
                ),
                cell: (info) => info.row.original.policyHolder || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('divisionHeader', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Risiko"
                    />
                ),
                cell: (info) => info.row.original.divisionHeader || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('risk', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Gefahr"
                    />
                ),
                cell: (info) => info.row.original.risk || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('objectHeader', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Objekt"
                    />
                ),
                cell: (info) => info.row.original.objectHeader || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('reportDate', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Meldedatum"
                    />
                ),
                cell: (info) =>
                    info.row.original.reportDate
                        ? toHumanReadable(fromIso(info.row.original.reportDate), 'date_short_2digit')
                        : '—',
                filterFn: 'dateFilter',
                meta: {
                    filter: { type: 'date' },
                },
            }),
            columnHelper.accessor('claimDate', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadendatum"
                    />
                ),
                cell: (info) =>
                    info.row.original.claimDate
                        ? toHumanReadable(fromIso(info.row.original.claimDate), 'date_short_2digit')
                        : '—',
                filterFn: 'dateFilter',
                meta: {
                    filter: { type: 'date' },
                },
            }),
            columnHelper.accessor('incidentLocation', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenadresse"
                    />
                ),
                cell: (info) => info.row.original.incidentLocation || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('claimType', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenart"
                    />
                ),
                cell: (info) => info.row.original.claimType || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('claimAmountEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Schadenhöhe"
                    />
                ),
                cell: (info) => (info.row.original.claimAmountEur ? formatEur(info.row.original.claimAmountEur) : '—'),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('deductibleEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Selbstbehalt"
                    />
                ),
                cell: (info) =>
                    info.row.original.deductibleEur !== null ? formatEur(info.row.original.deductibleEur) : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('quoteCount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="KVA #"
                    />
                ),
                cell: (info) => (info.row.original.quoteCount !== null ? info.row.original.quoteCount : '—'),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('quoteAmountEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="KVA €"
                    />
                ),
                cell: (info) =>
                    info.row.original.quoteAmountEur !== null ? formatEur(info.row.original.quoteAmountEur) : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('invoiceCount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="RG #"
                    />
                ),
                cell: (info) => (info.row.original.invoiceCount !== null ? info.row.original.invoiceCount : '—'),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('invoiceAmountEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="RG €"
                    />
                ),
                cell: (info) =>
                    info.row.original.invoiceAmountEur !== null ? formatEur(info.row.original.invoiceAmountEur) : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('selfRepairCount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="EL #"
                    />
                ),
                cell: (info) => (info.row.original.selfRepairCount !== null ? info.row.original.selfRepairCount : '—'),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('selfRepairAmountEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="EL €"
                    />
                ),
                cell: (info) =>
                    info.row.original.selfRepairAmountEur !== null
                        ? formatEur(info.row.original.selfRepairAmountEur)
                        : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('totalPayoutEur', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="filed-claims"
                        table={context.table}
                        column={context.column}
                        title="Ausgezahlt"
                    />
                ),
                cell: (info) =>
                    info.row.original.totalPayoutEur !== null ? formatEur(info.row.original.totalPayoutEur) : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
        ],
        [handleClaimClick]
    );

    if (isLoading) {
        return (
            <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner size="large" label="Lade eingereichte Schäden..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <Body1 style={{ color: 'var(--colorPaletteRedForeground1)' }}>
                    Fehler beim Laden der Daten: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
                </Body1>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <DataTable<FiledClaim, any>
                id="filed-claims"
                tableLabelNode={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div>Eingereichte Schäden mit</div>
                        <Dropdown
                            selectedOptions={[dateField]}
                            value={dateField === 'reportDate' ? 'Meldedatum' : 'Schadendatum'}
                            onOptionSelect={(_, d) =>
                                setDateField((d.optionValue as 'reportDate' | 'claimDate') || 'reportDate')
                            }
                            size="small"
                            style={{
                                minWidth: 0,
                                borderBottomColor: tokens.colorNeutralStroke1,
                            }}
                        >
                            <Option value="reportDate">Meldedatum</Option>
                            <Option value="claimDate">Schadendatum</Option>
                        </Dropdown>
                        <div>im Jahr</div>
                        <Dropdown
                            selectedOptions={[year.toString()]}
                            value={year.toString()}
                            onOptionSelect={(_, d) => setYear(parseInt(d.optionValue || currentYear.toString(), 10))}
                            size="small"
                            style={{
                                minWidth: 0,
                                borderBottomColor: tokens.colorNeutralStroke1,
                            }}
                        >
                            {yearOptions.map((y) => (
                                <Option key={y} value={y.toString()} text={y.toString()}>
                                    {y}
                                </Option>
                            ))}
                        </Dropdown>
                        <Hint text="Zahlungsbelege und Belegsummen erscheinen, sobald diese von der KI oder einem Sachbearbeiter bearbeitet wurden." />
                    </div>
                }
                loading={isLoading}
                data={data?.body.claims}
                columns={columns as any}
                getItemId={(item) => item.id}
                columnTitles={{
                    claimNumber: 'Schadennummer',
                    status: 'Status',
                    contractNumber: 'Vertrag',
                    policyHolder: 'Versicherungsnehmer',
                    divisionHeader: 'Risiko',
                    risk: 'Gefahr',
                    objectHeader: 'Objekt',
                    reportDate: 'Meldedatum',
                    claimDate: 'Schadendatum',
                    incidentLocation: 'Schadenadresse',
                    claimType: 'Schadenart',
                    claimAmountEur: 'Schadenhöhe',
                    deductibleEur: 'Selbstbehalt',
                    quoteCount: 'KVA #',
                    quoteAmountEur: 'KVA €',
                    invoiceCount: 'RG #',
                    invoiceAmountEur: 'RG €',
                    selfRepairCount: 'EL #',
                    selfRepairAmountEur: 'EL €',
                    totalPayoutEur: 'Ausgezahlt',
                }}
                defaultSorting={[
                    {
                        id: 'reportDate',
                        desc: true,
                    },
                ]}
                customToolbarActions={
                    <Button appearance="secondary" icon={<AddRegular />} onClick={handleNewClaimClick} size="small">
                        Schaden anlegen
                    </Button>
                }
                generateCustomToolbarCounts={(rows) => [
                    {
                        label: 'Anzahl Schäden',
                        count: rows.length,
                    },
                ]}
                dataExportFunc={(claims) => {
                    const header: string[][] = [
                        [
                            'Schadennummer',
                            'Status',
                            'Vertrag',
                            'Versicherungsnehmer',
                            'Risiko',
                            'Gefahr',
                            'Objekt',
                            'Meldedatum',
                            'Schadendatum',
                            'Schadenadresse',
                            'Schadenart',
                            'Schadenhöhe',
                            'Selbstbehalt',
                            'KVA #',
                            'KVA €',
                            'RG #',
                            'RG €',
                            'EL #',
                            'EL €',
                            'Ausgezahlt',
                        ],
                    ];
                    const data: string[][] = claims.map((claim) => [
                        claim.claimNumber,
                        claim.status ? claim.status : '-',
                        claim.contractNumber ? claim.contractNumber : '-',
                        claim.policyHolder ? claim.policyHolder : '-',
                        claim.divisionHeader ? claim.divisionHeader : '-',
                        claim.risk ? claim.risk : '-',
                        claim.objectHeader ? claim.objectHeader : '-',
                        claim.reportDate ? toHumanReadable(fromIso(claim.reportDate), 'date_short_2digit') : '-',
                        claim.claimDate ? toHumanReadable(fromIso(claim.claimDate), 'date_short_2digit') : '-',
                        claim.incidentLocation ? claim.incidentLocation : '-',
                        claim.claimType ? claim.claimType : '-',
                        claim.claimAmountEur !== null ? formatEur(claim.claimAmountEur) : '-',
                        claim.deductibleEur !== null ? formatEur(claim.deductibleEur) : '-',
                        claim.quoteCount !== null ? claim.quoteCount.toString() : '-',
                        claim.quoteAmountEur !== null ? formatEur(claim.quoteAmountEur) : '-',
                        claim.invoiceCount !== null ? claim.invoiceCount.toString() : '-',
                        claim.invoiceAmountEur !== null ? formatEur(claim.invoiceAmountEur) : '-',
                        claim.selfRepairCount !== null ? claim.selfRepairCount.toString() : '-',
                        claim.selfRepairAmountEur !== null ? formatEur(claim.selfRepairAmountEur) : '-',
                        claim.totalPayoutEur !== null ? formatEur(claim.totalPayoutEur) : '-',
                    ]);
                    return {
                        identifier: 'Eingereichte_Schäden',
                        header,
                        data,
                    };
                }}
            />
        </div>
    );
}
